import React, { useCallback, useEffect, useState } from 'react';
import { useApiAuth } from '../../hooks/useApiAuth';

const DEFAULT_ABOUT = {
  heading: 'About Us',
  mission: 'Our mission is to create meaningful, handcrafted pieces that bring beauty, function, and a personal touch into everyday life. We aim to turn simple materials into lasting items people can cherish and share.',
  paragraphs: [
    'At Kalsies Creations / California Handcraft, everything we make is created by hand with care, creativity, and attention to detail. We love turning simple materials into meaningful, one-of-a-kind pieces that people can use, gift, and enjoy every day.',
    'We specialize in handcrafted wood items, including cutting boards, signs, planters, trellises, and other unique pieces for the home. Many of our items can be customized with laser engraving, but we also offer beautiful, non-engraved pieces for a more natural look.',
    'In addition to our woodwork, we create handmade greeting cards and jewelry—perfect for adding a personal touch to any occasion. Whether custom or ready-made, our goal is to create pieces that feel special, thoughtful, and made just for you.',
  ],
  values: [
    { title: 'Quality', description: 'We strive for the highest quality in every project.' },
    { title: 'Passion & Authenticity', description: 'We pour our heart into every piece we create with honesty and accountability at the core of everything we do.' },
    { title: 'Timeliness', description: 'We work closely with our clients to achieve shared goals.' },
  ],
  team: [
    { name: 'James Curtis', role: 'Artisan and Wood Worker', bio: '', image_url: '/james-photo.jpg' },
    { name: 'Gina Curtis', role: 'Designer and Laser Engraving Specialist', bio: '', image_url: '/Gina photo.jpg' },
  ],
};

export default function AboutManager({ setStatus }) {
  const { authFetch } = useApiAuth();
  const [busy, setBusy] = useState(false);
  const [about, setAbout] = useState(DEFAULT_ABOUT);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      const a = data.about || {};
      setAbout({
        heading: a.heading || DEFAULT_ABOUT.heading,
        mission: a.mission || DEFAULT_ABOUT.mission,
        paragraphs: Array.isArray(a.paragraphs) && a.paragraphs.length ? a.paragraphs : DEFAULT_ABOUT.paragraphs,
        values: Array.isArray(a.values) && a.values.length ? a.values : DEFAULT_ABOUT.values,
        team: Array.isArray(a.team) && a.team.length ? a.team : DEFAULT_ABOUT.team,
      });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'about', value: about } });
      setStatus('Saved about.');
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  const uploadFor = async (i, file) => {
    setBusy(true); setStatus('Uploading…');
    try {
      const up = await authFetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
      const team = about.team.slice();
      team[i] = { ...team[i], image_url: up.url };
      setAbout({ ...about, team });
      setStatus('Photo uploaded. Click Save About.');
    } catch (e) { setStatus(`Upload error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div>
      <section className="admin-section">
        <h2>About page heading & mission</h2>
        <div className="admin-form">
          <label>About page heading
            <input type="text" value={about.heading} onChange={(e) => setAbout({ ...about, heading: e.target.value })} />
          </label>
          <label>Mission statement
            <textarea rows="3" value={about.mission} onChange={(e) => setAbout({ ...about, mission: e.target.value })} />
          </label>
        </div>
      </section>

      <section className="admin-section">
        <h2>About page paragraphs</h2>
        <div className="admin-form">
          {about.paragraphs.map((p, i) => (
            <div key={i} className="admin-card">
              <textarea rows="3" value={p} onChange={(e) => {
                const next = about.paragraphs.slice(); next[i] = e.target.value; setAbout({ ...about, paragraphs: next });
              }} />
              <button className="admin-btn" onClick={() => setAbout({ ...about, paragraphs: about.paragraphs.filter((_, j) => j !== i) })}>Remove</button>
            </div>
          ))}
          <button className="admin-btn" onClick={() => setAbout({ ...about, paragraphs: [...about.paragraphs, ''] })}>+ Add paragraph</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Our Values boxes</h2>
        <div className="admin-form">
          {about.values.map((v, i) => (
            <div key={i} className="admin-card">
              <label>Title
                <input type="text" value={v.title || ''} onChange={(e) => {
                  const next = about.values.slice(); next[i] = { ...next[i], title: e.target.value }; setAbout({ ...about, values: next });
                }} />
              </label>
              <label>Description
                <textarea rows="2" value={v.description || ''} onChange={(e) => {
                  const next = about.values.slice(); next[i] = { ...next[i], description: e.target.value }; setAbout({ ...about, values: next });
                }} />
              </label>
              <button className="admin-btn" onClick={() => setAbout({ ...about, values: about.values.filter((_, j) => j !== i) })}>Remove</button>
            </div>
          ))}
          <button className="admin-btn" onClick={() => setAbout({ ...about, values: [...about.values, { title: '', description: '' }] })}>+ Add value</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Meet Our Team</h2>
        <div className="admin-form">
          {about.team.map((m, i) => (
            <div key={i} className="admin-card">
              <label>Name
                <input type="text" value={m.name || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], name: e.target.value }; setAbout({ ...about, team: t }); }} />
              </label>
              <label>Role
                <input type="text" value={m.role || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], role: e.target.value }; setAbout({ ...about, team: t }); }} />
              </label>
              <label>Bio
                <textarea rows="3" value={m.bio || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], bio: e.target.value }; setAbout({ ...about, team: t }); }} />
              </label>
              <label>Image URL
                <input type="text" value={m.image_url || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], image_url: e.target.value }; setAbout({ ...about, team: t }); }} />
              </label>
              <label>Upload photo
                <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFor(i, f); }} />
              </label>
              {m.image_url && <img src={m.image_url} alt="preview" style={{ maxWidth: 120, borderRadius: '50%' }} />}
              <button className="admin-btn" onClick={() => setAbout({ ...about, team: about.team.filter((_, j) => j !== i) })}>Remove</button>
            </div>
          ))}
          <button className="admin-btn" onClick={() => setAbout({ ...about, team: [...about.team, { name: '', role: '', bio: '', image_url: '' }] })}>+ Add team member</button>
        </div>
      </section>

      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save About</button>
    </div>
  );
}
